import {Component, ElementRef, OnDestroy, OnInit, ViewEncapsulation} from '@angular/core';
import { AbstractControl, FormControl, FormGroup, ValidationErrors, Validators } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { Logger } from 'src/util/logger';
import { Address } from '../../gen/model/address';
import { AuthenticationChallenge } from '../../gen/model/authenticationChallenge';
import { UserForRegisterUser } from '../../gen/model/userForRegisterUser';
import ResponseUtil from '../../util/responseUtil';
import { AppConstants } from '../AppConstants';
import { CountriesService } from '../countries-service';
import { FormInputComponent } from '../form-input/form-input.component';
import { LoginBaseComponent } from '../login-base.component';
import { ParamsService } from '../params.service';
import {AlertHandler} from '../no-concurrent-alerts-handler';
import {cardFooterFeedbackTransition} from '../animations';
import {AuthenticationInfoService} from '../authentication-info.service';
import LocationUtil, {UrlParams, getAppBaseHref} from '../../util/locationUtil';
import {PlatformLocation} from '@angular/common';
import {TranslateService} from '@ngx-translate/core';
import {Observable} from 'rxjs';

const _ = (s) => s;

interface Item {
  value: string;
  label: string;
}
@Component({
  selector: 'app-register',
  templateUrl: './register.component.html',
  styleUrls: ['./register.component.scss'],
  encapsulation: ViewEncapsulation.None,
  animations: [
    cardFooterFeedbackTransition,
  ],
})
export class RegisterComponent extends LoginBaseComponent implements OnInit, OnDestroy {

  registerForm = new FormGroup({
    firstName: new FormControl('', [Validators.required]),
    lastName: new FormControl('', [Validators.required]),
    email: new FormControl('', [Validators.required, Validators.email]),
    password: new FormControl('', [Validators.required, Validators.pattern(AppConstants.PASSWORD_PATTERN)]),
    confirmPassword: new FormControl('', [Validators.required]),
    country: new FormControl(null, [Validators.required, Validators.pattern(AppConstants.NOT_NULL_PATTERN)]),
    acceptTsAndCs: new FormControl(false, [Validators.requiredTrue]),
  });

  email: string;
  emailAsReadOnly = false;
  countryList: Item[];

  private errorBody: any;
  private genericError = false;

  constructor(
    private authnInfoService: AuthenticationInfoService,
    private countries: CountriesService,
    route: ActivatedRoute,
    router: Router,
    private logger: Logger,
    private alertHandler: AlertHandler,
    private paramsService: ParamsService,
    private translate: TranslateService,
    hostElement: ElementRef,
    private platformLocation: PlatformLocation
  ) {
    super(route, router, hostElement);
  }

  ngOnInit() {
    super.init();
    this.authnInfoService.startTask('register');
    this.countries.alphabetical().subscribe(res => {
      this.countryList = res.map((n) => ({label: n.name, value: n.code}));
    });
    this.validateConfirmPassword = this.validateConfirmPassword.bind(this);
    this.registerForm.setValidators([this.validateConfirmPassword]);
    this.subscribeParamsState();
  }
  ngOnDestroy() {
    if (this.authnInfoService.sessionInfo.value.tasksInProgress.indexOf('register') >= 0) {
      this.authnInfoService.cancelTask('register');
    }
  }

  isFieldInvalid(field: string): boolean {
    return this.hasError(field) || this.isInputInvalid(field);
  }

  isInputInvalid(field: string): boolean {
    if (field === 'confirmPassword') {
      return (this.registerForm.get(field).invalid
        || (this.registerForm.invalid && this.registerForm.errors && this.registerForm.errors.mustMatch))
        && this.registerForm.get(field).touched;
    } else {
      return this.registerForm.get(field).invalid && this.registerForm.get(field).touched;
    }
  }

  isFieldValid(field: string): boolean {
    return !this.hasError(field) && this.isInputValid(field);
  }

  isInputValid(field: string): boolean {
    if (field === 'confirmPassword') {
      return (this.registerForm.get(field).valid
        && (this.registerForm.valid || !this.registerForm.errors || !this.registerForm.errors.mustMatch))
        && this.registerForm.get(field).touched;
    } else {
      return this.registerForm.get(field).valid && this.registerForm.get(field).touched;
    }
  }

  hasError(field?: string, code?: string): boolean {
    let retValue = false;
    if (field) {
      if (this.errorBody &&
          this.errorBody.error &&
          this.errorBody.childErrors &&
          this.errorBody.childErrors[field]) {
        if (code) {
          if (this.errorBody.childErrors[field].error === code) {
            retValue = true;
          }
        } else {
          retValue = true;
        }
      }
    } else if (this.genericError) {
      retValue = true;
    }
    return retValue;
  }

  onSubmit() {
    this.errorBody = undefined;
    this.genericError = false;
    const address: Address = {
      country: this.registerForm.get('country').value,
    };
    const userToRegister: UserForRegisterUser = {
      email: this.registerForm.get('email').value,
      firstName: this.registerForm.get('firstName').value,
      lastName: this.registerForm.get('lastName').value,
      password: this.registerForm.get('password').value,
      address: address,
    };
    this.logger.debug('Registering...');
    const verificationUri = [];
    verificationUri.push(LocationUtil.getOwnDomainURL());
    verificationUri.push(getAppBaseHref(this.platformLocation));
    verificationUri.push(AppConstants.ROUTE_VALIDATE_EMAIL);
    if (verificationUri[0].indexOf('?') < 0) {
      verificationUri.push('?');
    } else {
      verificationUri.push('&');
    }
    verificationUri.push(AppConstants.QP_EMAIL_VALIDATION_KEY);
    verificationUri.push('={0}');
    const params: UrlParams = LocationUtil.getParams([AppConstants.QP_EMAIL_VALIDATION_KEY]);
    const keys = Object.keys(params);
    for (let i = 0; i < keys.length; i += 1) {
      verificationUri.push('&');
      verificationUri.push(keys[i]);
      verificationUri.push('=');
      verificationUri.push(params[keys[i]]);
    }
    this.authnInfoService.registerUser(userToRegister, true, verificationUri.join(''), undefined)
      .subscribe(response => {
        this.logger.debug('Registration completed, response %d', response.status);
        this.authnInfoService.completeTask('register');
      }, errorResponse => {
        this.logger.debug('Registration failed, response %O', errorResponse);
        const challenges = ResponseUtil.readChallenges(errorResponse).required;
        let emailChallenge: AuthenticationChallenge = null;
        if (challenges && challenges.length) {
          emailChallenge = challenges.find(challenge => AppConstants.AC_AM_VERIFY_EMAIL === challenge.authenticationMethod);
        }
        if (emailChallenge) {
          // Email challenge is ok as it's handled in another component
          this.authnInfoService.completeTask('register');
        } else if (errorResponse && errorResponse.error) {
          this.errorBody = errorResponse.error;
          this.genericError = true;
        }
      })
    ;
  }

  resolveCountryOptions(obj?: Item): Item[] {
    let t = null;
    if (this.countryList) {
      t = [].concat(this.countryList);
      if (obj) {
        t.unshift(obj);
      }
    }
    return t;
  }

  // noinspection JSMethodCanBeStatic
  resolveFormInputWrapperClass(): string {
    return FormInputComponent.WRAPPER_CLASS;
  }

  // noinspection JSMethodCanBeStatic
  resolveFormInputLabelClass(): string {
    return FormInputComponent.LABEL_CLASS;
  }

  // noinspection JSMethodCanBeStatic
  resolveFormInputControlWrapperClass(): string {
    return FormInputComponent.CONTROL_WRAPPER_CLASS;
  }

  private subscribeParamsState() {
    this.route.queryParams.subscribe(params => {
      const keyEmailParams = params[AppConstants.QP_EMAIL];
      let email;
      if (keyEmailParams) {
        if (Array.isArray(keyEmailParams)) {
          email = keyEmailParams[0];
        } else {
          email = keyEmailParams;
        }
      }
      this.email = email;
      if (this.email) {
        this.registerForm.get('email').setValue(this.email);
      }
      if (params[AppConstants.QP_INVITATION_TOKEN] &&
          params[AppConstants.QP_REQUIRE_CHALLENGE] === AppConstants.AC_AM_INVITATION_ACCEPTED &&
          this.email
      ) {
        this.emailAsReadOnly = true;
      }
    });
  }

  private validateConfirmPassword(control: AbstractControl): ValidationErrors | null {
    const pwField = this.registerForm.get('password');
    const cpwField = this.registerForm.get('confirmPassword');
    return cpwField.value === pwField.value
      ? null
      : { 'mustMatch': true };
  }

  public resolveWindowTitlePart(): Observable<string|undefined> {
    return this.translate.get(_('register.window-title'));
  }
}
